import React, { useEffect, useState } from 'react';
import {
  DbQuestionData,
  Player,
  DetailedPlayer,
  ReportInaccuracy,
  Mode,
  ChatMessage,
  Spectator,
  VanquishedDemon,
} from '../../types';
import {
  Badge,
  Box,
  Button,
  Drawer,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ReportInaccuraciesModal } from '../../../../modals/ReportInaccuraciesModal';
import { Socket } from 'socket.io-client';
import Confetti from 'react-confetti';
import { WeekendOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Victory } from '../Victory';
import { Achievements } from '../Achievements';
import { LeaderboardDrawer } from './LeaderboardDrawer';
import { ScorecardMulti } from '../scorecard-multi';
import { MmrResults } from '../../../../dialog/MmrResults';
import { SurvivalStreakMessage } from '../SurvivalStreakMessage';
import { BanCategoriesInfoDialog } from '../../../../dialog/BanCategoriesInfoDialog';
import { fetchGet, fetchPost } from '@par/shared/api/common';
import { Game, Team, Teams } from '@par/shared/types/game';
import { BottomDrawer } from './BottomDrawer';
import { BlinkingChatButton } from './BlinkingChatButton';
import { DetailedUserAchievement, Profile } from '@par/shared/types/player';
import { StarterKitConfig } from '../../../starter-kits/types';
import { sortPlayers } from '../..';
import { TournamentLeaderboardDrawer } from './TournamentLeaderboard';
import { ScoreCardStats } from './ScoreCardStats';
import { ToggleStats } from './ToggleStats';

const poolLeaderboardDrawerWidth = 280;
const nextRoundDrawerHeight = 140;

type PropTypes = {
  averageDifficulty?: number;
  bannedCategories?: string[];
  categorySelections?: string[];
  categorySet?: string;
  cutoff?: number;
  daily?: boolean;
  detailedPlayers?: Record<string, DetailedPlayer>;
  detailedUserAchievements?: DetailedUserAchievement[];
  demons?: boolean;
  demonsVanquished?: VanquishedDemon[];
  fromMain?: boolean;
  fromSoloPunchcard?: boolean;
  fromStarterKit?: boolean;
  gameId: string;
  gameOver: boolean;
  gameType?: 'casual' | 'pro';
  handicap?: number;
  handleJoinGameClick: (gameId: string, mode: Mode) => void;
  handleLeaveGameClick: (gameId: string, profileId: string) => void;
  handlePoolLeaderboardUsernameClick: (profileId: string) => void;
  isDemo?: boolean;
  isGhostChallenge?: boolean;
  isMatchMaking?: boolean;
  league?: boolean;
  lobbyOpenGames: Game[];
  mode: Mode;
  myTeam?: Team;
  otherTeam?: Team;
  nextRoundStartTime?: number;
  onCategoryBanClick: () => void;
  onUpdateProfile: () => void;
  profile: Profile;
  questionSet: DbQuestionData[];
  ranked: boolean;
  reportInaccuracy?: ReportInaccuracy[];
  roundOver: boolean;
  seedDetails?: { submittedBy: string; submittedByRating: number };
  selectedPlayer?: DetailedPlayer;
  socket: Socket;
  spectate?: boolean;
  spectatorCount: number;
  spectatorList: Spectator[];
  starterKit?: StarterKitConfig;
  survivalMode?: 'endurance' | 'challenge' | 'demon';
  tournament?: number;
  tournamentTotalPlayers?: number;
  variant?: 'normal' | 'survival' | 'starter-kit' | 'turbo';
};

export const ScoreScreen = ({
  averageDifficulty,
  bannedCategories,
  categorySelections,
  categorySet,
  cutoff,
  daily,
  demons,
  demonsVanquished,
  detailedPlayers,
  detailedUserAchievements,
  fromMain,
  fromSoloPunchcard,
  fromStarterKit,
  gameId,
  gameOver,
  gameType,
  handicap,
  handleJoinGameClick,
  handleLeaveGameClick,
  handlePoolLeaderboardUsernameClick,
  isDemo,
  isGhostChallenge,
  isMatchMaking,
  league,
  lobbyOpenGames,
  mode,
  myTeam,
  otherTeam,
  nextRoundStartTime,
  profile,
  onCategoryBanClick,
  onUpdateProfile,
  questionSet,
  ranked,
  reportInaccuracy,
  roundOver,
  seedDetails,
  selectedPlayer,
  socket,
  spectate,
  spectatorCount,
  spectatorList,
  starterKit,
  survivalMode,
  tournament,
  tournamentTotalPlayers,
  variant,
}: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [sortedPlayers, setSortedPlayers] = useState<DetailedPlayer[]>();
  const [openReportInaccuracyModal, setOpenReportInaccuracyModal] =
    useState(false);
  const [reportedInaccuracies, setReportedInaccuracies] =
    useState<ReportInaccuracy[]>();
  const [enableConfetti, setEnableConfetti] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [keepChatClosed, setKeepChatClosed] = useState(false);
  const [detailedPlayerSelf, setDetailedPlayerSelf] =
    useState<DetailedPlayer>();
  const [detailedPlayerThem, setDetailedPlayerThem] =
    useState<DetailedPlayer>();
  const [scoreScreenChat, setScoreScreenChat] = useState<ChatMessage[]>();
  const [incomingChat, setIncomingChat] =
    useState<{ username: string; text: string; leftChat: boolean }[]>();
  const [chat, setChat] = useState<
    {
      color?: string;
      username: string;
      text: string;
      avatarPublicId: string;
      leftChat: boolean;
    }[]
  >([]);
  const [lastReadChatLength, setLastReadChatLength] = useState(0);
  const [unreadChat, setUnreadChat] = useState(false);
  const [openMmrResults, setOpenMmrResults] = useState(false);
  const [displayBanCategoriesInfoDialog, setDisplayBanCategoriesInfoDialog] =
    useState(false);
  const [focusInput, setFocusInput] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [openBottomDrawer, setOpenBottomDrawer] = useState(
    mode === 'solo' ? false : true
  );
  const [openedBottomDrawerByClick, setOpenedBottomDrawerByClick] =
    useState(true);
  const [toggleQuestionsStats, setToggleQuestionsStats] = useState<
    'scorecard' | 'stats'
  >('scorecard');
  const [replayUrl, setReplayUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!gameOver || !gameId) return;
    const MAX_ATTEMPTS = 6;
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts++;
      try {
        const data = await fetchGet(`/games/${gameId}/replay`);
        if (data?.url) {
          setReplayUrl(data.url);
          clearInterval(timer);
        }
      } catch {
        // 404 = not ready yet; keep trying
      }
      if (attempts >= MAX_ATTEMPTS) clearInterval(timer);
    }, 3000);
    return () => clearInterval(timer);
  }, [gameOver, gameId]);

  useEffect(() => {
    updateShowConfetti();

    const keyDownHandler = (event: any) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpenedBottomDrawerByClick(false);
        setOpenBottomDrawer(false);
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        setOpenedBottomDrawerByClick(false);
        setOpenBottomDrawer(true);
        setFocusInput((prev) => prev + 1);
      }
    };
    document.addEventListener('keydown', keyDownHandler);

    socket.on('chat', setIncomingChat);
    socket.emit('requestChatHistory', {
      gameId,
      profileId: profile.id,
    });

    setToggleQuestionsStats('scorecard');

    window.addEventListener('popstate', () => emitLeaveChat());
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
      window.removeEventListener('popstate', () => emitLeaveChat());
    };
  }, []);

  useEffect(() => {
    if (!detailedPlayers) return;
    const sortedPlayers = sortPlayers(detailedPlayers) as DetailedPlayer[];
    setSortedPlayers(sortedPlayers);
    if (spectate) {
      setupPlayersAsSpectator(detailedPlayers);
      return;
    }
    setDetailedPlayerSelf(detailedPlayers[profile.id]);
    if (mode === 'match' || mode === 'bracket') {
      const playerThemProfileId = Object.keys(detailedPlayers).find(
        (profileId) => profileId !== profile.id
      );
      if (playerThemProfileId)
        setDetailedPlayerThem(detailedPlayers[playerThemProfileId]);
    }
  }, [detailedPlayers]);

  const setupPlayersAsSpectator = (
    detailedPlayers: Record<string, DetailedPlayer>
  ) => {
    setDetailedPlayerSelf(Object.values(detailedPlayers)[0]);
    // if (mode === 'solo')
    if (mode === 'match' || mode === 'bracket') {
      setDetailedPlayerThem(Object.values(detailedPlayers)[1]);
    }
  };

  useEffect(() => {
    if (
      gameOver &&
      detailedPlayerSelf &&
      detailedPlayerThem &&
      detailedPlayerSelf.parScore < detailedPlayerThem.parScore
    ) {
      setEnableConfetti(true);
      return;
    }
  }, [detailedPlayerThem]);

  useEffect(() => {
    const inGameReportedInaccuracies = reportInaccuracy?.filter(
      (e) => e.profileId === profile.id
    );
    if (inGameReportedInaccuracies && inGameReportedInaccuracies?.length > 0) {
      setOpenReportInaccuracyModal(true);
      setReportedInaccuracies(inGameReportedInaccuracies);
    }
  }, [reportInaccuracy]);

  useEffect(() => {
    const filteredChat = chat.filter((c) => !c.leftChat);
    if (filteredChat.length > lastReadChatLength && !openBottomDrawer)
      setUnreadChat(true);
  }, [chat]);

  useEffect(() => {
    if (!incomingChat || !detailedPlayers) return;
    const playerAvatarPublicIds = Object.values(detailedPlayers).map((p) => ({
      avatarPublicId: p.avatarPublicId,
      username: p.username,
    }));
    const spectatorAvatarUrls = spectatorList?.map((s) => ({
      avatarPublicId: s.avatarPublicId,
      username: s.username,
    }));
    const userAvatarPublicIds =
      playerAvatarPublicIds?.concat(spectatorAvatarUrls);
    const chatWithAvatars = incomingChat.map((c) => ({
      ...c,
      avatarPublicId: !spectatorAvatarUrls
        ? ''
        : userAvatarPublicIds?.find((u) => u.username === c.username)
            ?.avatarPublicId || '',
    }));
    setChat(chatWithAvatars);
  }, [incomingChat]);

  useEffect(() => {
    if (profile.firstMathFormulaSeen) return;
    if (!questionSet || !ranked) return;
    if (profile.bannedCategories && profile.bannedCategories.length) return;
    const filterForMathFormulas = questionSet.filter((q) => q.mathFormula);
    if (filterForMathFormulas.length) {
      setDisplayBanCategoriesInfoDialog(true);
      updateFirstMathFormulaSeen(profile.id);
    }
  }, [questionSet]);

  useEffect(() => {
    if (openBottomDrawer) {
      setUnreadChat(false);
    }
  }, [openBottomDrawer]);

  const updateFirstMathFormulaSeen = async (profileId: string) => {
    const updatedProfile = {
      profileId: profileId,
    };
    await fetchPost('/profile/first-math-formula-seen', updatedProfile);
    onUpdateProfile();
  };

  const clearChat = () => setChat((prev) => []);

  const handleReportInaccuracyRowClick = (
    row: DbQuestionData,
    questionIndex: number
  ) => {
    if (reportedInaccuracies?.find((e) => e.questionIndex === questionIndex)) {
      setOpenReportInaccuracyModal(true);
      return;
    }
    const newReport: ReportInaccuracy = {
      comment: undefined,
      questionId: row._id,
      questionIndex: questionIndex,
      profileId: profile.id,
      submitted: false,
    };
    const updatedReportInaccuracies = reportedInaccuracies
      ? reportedInaccuracies.concat(newReport)
      : [newReport];
    setReportedInaccuracies(updatedReportInaccuracies);
    setOpenReportInaccuracyModal(true);
  };

  const handleInaccuracyCommentOnChange = (
    questionIndex: number,
    text: string
  ) => {
    if (!reportedInaccuracies) return;
    const report = reportedInaccuracies.find(
      (r) => r.questionIndex === questionIndex
    );
    if (report) {
      report.comment = text;
      setReportedInaccuracies(reportedInaccuracies);
    }
  };

  const handleReportSubmit = (questionIndex: number) => {
    const found = reportedInaccuracies?.find(
      (e) => e.questionIndex === questionIndex
    );
    if (!found || !found.comment) return;
    found.submitted = true;
    const updatedReportInaccuracies = reportedInaccuracies?.filter(
      (e) => e.questionIndex !== questionIndex
    );
    socket.emit('reportInaccuracyComment', found);
    setReportedInaccuracies(updatedReportInaccuracies?.concat(found));
  };

  const handleBackToLobbyClick = () => {
    // update profiles so things like search credits, mmr, are updated when user goes back
    onUpdateProfile();
    emitLeaveChat();
    navigate('/lobby');
  };

  const handleToDailiesPage = () => {
    onUpdateProfile();
    navigate('/explore/dailies');
  };

  const handleBackToTournamentLobbyClick = () => {
    onUpdateProfile();
    emitLeaveChat();
    navigate('/tournament');
  };

  const handleBackToLeagueLobbyClick = () => {
    onUpdateProfile();
    emitLeaveChat();
    navigate('/league/lounge');
  };

  const handleToSoloPunchcardPage = () => {
    onUpdateProfile();
    navigate(`/profile/punchcard/${profile.id}`, {
      state: {
        showSoloPunchcard: true,
      },
    });
  };

  const handleToMainPage = () => {
    onUpdateProfile();
    navigate(`/main`);
  };

  const handleToStarterKit = () => {
    onUpdateProfile();
    navigate(`/explore/starter-kits`);
  };

  const getVictoryMessage = () => {
    if (!detailedPlayerSelf || !detailedPlayerThem) return;
    let type: 'win' | 'loss' | 'tie' = 'win';
    let winnerUsername = '';
    // spectators will see the Victory "win" message
    if (detailedPlayerSelf.parScore < detailedPlayerThem.parScore || spectate) {
      type = 'win';
      winnerUsername = detailedPlayerSelf.username;
    }
    if (detailedPlayerSelf.parScore > detailedPlayerThem.parScore) {
      type = 'loss';
      winnerUsername = detailedPlayerThem.username;
    }
    if (detailedPlayerSelf.parScore === detailedPlayerThem.parScore) {
      type = 'tie';
    }
    return (
      <Victory
        playerMeAvatarUrl={detailedPlayerSelf.avatarUrl}
        playerThemAvatarUrl={detailedPlayerThem?.avatarUrl}
        type={type}
        winnerUsername={winnerUsername}
      ></Victory>
    );
  };

  const showSurvivalStreakMessage = () => {
    if (!detailedPlayerSelf || spectate) return false;
    const questionsSurvived = questionSet.length - 1;
    const finalScore = detailedPlayerSelf.parScore;
    if (survivalMode === 'endurance') {
      if (!profile.survivalStats?.endurance?.bestScore?.score) {
        return true;
      }
      if (finalScore < profile.survivalStats?.endurance?.bestScore?.score) {
        return true;
      }
      if (!profile.survivalStats?.endurance?.longestStreak?.questionsSurvived) {
        return true;
      }
      if (
        questionsSurvived >
        profile.survivalStats?.endurance?.longestStreak?.questionsSurvived
      ) {
        return true;
      }
    }
    if (survivalMode === 'challenge') {
      if (!profile.survivalStats?.challenge?.allTime?.bestScore?.score) {
        return true;
      }
      if (
        finalScore < profile.survivalStats?.challenge?.allTime?.bestScore?.score
      ) {
        return true;
      }
      if (
        !profile.survivalStats?.challenge?.allTime?.longestStreak
          ?.questionsSurvived
      ) {
        return true;
      }
      if (
        questionsSurvived >
        profile.survivalStats?.challenge?.allTime?.longestStreak
          ?.questionsSurvived
      ) {
        return true;
      }
    }
    return false;
  };

  const getSurvivalStreakMessage = () => {
    if (!detailedPlayerSelf) return false;
    const questionsSurvived = questionSet.length - 1;
    const finalScore = detailedPlayerSelf.parScore;
    let newBestScore = false;
    let newSlayStreak = false;
    if (survivalMode === 'endurance') {
      if (finalScore < profile.survivalStats?.endurance?.bestScore?.score) {
        newBestScore = true;
      }
      if (
        questionsSurvived >
        profile.survivalStats?.endurance?.longestStreak?.questionsSurvived
      ) {
        newSlayStreak = true;
      }
    }
    if (survivalMode === 'challenge') {
      if (
        finalScore <
        (profile.survivalStats?.challenge?.allTime?.bestScore?.score || 10000)
      ) {
        newBestScore = true;
      }
      if (
        questionsSurvived >
        (profile.survivalStats?.challenge?.allTime?.longestStreak
          ?.questionsSurvived || 0)
      ) {
        newSlayStreak = true;
      }
    }
    return (
      <SurvivalStreakMessage
        finalScore={finalScore}
        newBestScore={newBestScore}
        newSlayStreak={newSlayStreak}
        questionsSurvived={questionsSurvived}
        playerMeAvatarUrl={detailedPlayerSelf.avatarUrl}
        username={detailedPlayerSelf.username}
      />
    );
  };

  const closeChat = () => {
    setLastReadChatLength(chat.length);
    setKeepChatClosed(true);
    setOpenChat(false);
  };
  const handleOpenChatClick = () => {
    setUnreadChat(false);
    setOpenChat(true);
  };

  const emitLeaveChat = () => {
    clearChat();
    socket.emit('chat', {
      gameId,
      username: profile.username,
      text: '',
      leftChat: true,
    });
  };

  const handleMmrResultsClick = () => {
    setOpenMmrResults(true);
  };

  const updateShowConfetti = () => {
    if (variant === 'survival') {
      if (showSurvivalStreakMessage()) {
        setShowConfetti(true);
        return;
      }
      setShowConfetti(false);
      return;
    }
    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
  };

  const getUsersInRoom = () => {
    if (!detailedPlayers) return [];
    const usernames = [];
    const playerUsernames = Object.values(detailedPlayers).map(
      (p) => p.username
    );
    usernames.push(...playerUsernames);
    if (spectatorList) {
      const spectatorUsernames = spectatorList.map((s) => s.username);
      usernames.push(...spectatorUsernames);
    }
    return usernames;
  };

  const handleCloseBottomDrawerClick = () => {
    setOpenedBottomDrawerByClick(false);
    setOpenBottomDrawer(false);
  };

  const handleOpenBottomDrawerClick = () => {
    setOpenedBottomDrawerByClick(true);
    setOpenBottomDrawer(true);
  };

  const handleShowStatsChange = (event: any, value: 'scorecard' | 'stats') => {
    if (value === null) return;
    setToggleQuestionsStats(value);
  };

  return (
    <>
      {displayBanCategoriesInfoDialog ? (
        <BanCategoriesInfoDialog
          close={() => setDisplayBanCategoriesInfoDialog(false)}
          onCategoryBanClick={onCategoryBanClick}
          open={displayBanCategoriesInfoDialog}
        />
      ) : null}
      {detailedPlayerSelf ? (
        <MmrResults
          closeMmrResults={() => setOpenMmrResults(false)}
          detailedPlayers={detailedPlayers}
          mode={mode}
          openMmrResults={openMmrResults}
          seedDetails={seedDetails}
        ></MmrResults>
      ) : null}
      {detailedPlayerSelf &&
      detailedPlayerSelf.achievementsAchieved &&
      detailedPlayerSelf.achievementsAchieved.length &&
      !spectate ? (
        <Achievements
          achievements={detailedPlayerSelf.achievementsAchieved}
          detailedUserAchievements={detailedUserAchievements}
          mode={mode}
        ></Achievements>
      ) : null}
      {detailedPlayerThem && !spectate ? getVictoryMessage() : null}
      {detailedPlayerSelf &&
      variant === 'survival' &&
      showSurvivalStreakMessage()
        ? getSurvivalStreakMessage()
        : null}
      {showConfetti ? (
        <Confetti
          numberOfPieces={1000}
          recycle={false}
          style={{ zIndex: 9999 }}
        />
      ) : null}
      <ReportInaccuraciesModal
        handleInaccuracyCommentOnChange={handleInaccuracyCommentOnChange}
        handleReportSubmit={handleReportSubmit}
        openReportInaccuracyModal={openReportInaccuracyModal}
        questionSet={questionSet}
        reportedInaccuracies={reportedInaccuracies}
        setOpenReportInaccuracyModal={setOpenReportInaccuracyModal}
      ></ReportInaccuraciesModal>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: `calc(100% - ${
            mode === 'sit-n-go' ? poolLeaderboardDrawerWidth : 0
          }px)`,
          paddingBottom: '300px', // chat and quickjoin footer
        }}
      >
        <Box sx={{ padding: 2, width: '100%' }}>
          {gameOver || roundOver ? (
            <Stack spacing={3} sx={{ position: 'absolute', left: 10 }}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<WeekendOutlined />}
                  onClick={
                    daily
                      ? handleToDailiesPage
                      : tournament
                      ? handleBackToTournamentLobbyClick
                      : league
                      ? handleBackToLeagueLobbyClick
                      : fromSoloPunchcard
                      ? handleToSoloPunchcardPage
                      : fromMain
                      ? handleToMainPage
                      : fromStarterKit
                      ? handleToStarterKit
                      : handleBackToLobbyClick
                  }
                >
                  {daily
                    ? 'To Dailies Page'
                    : tournament
                    ? 'Back to tourney lobby'
                    : league
                    ? 'Back to League Lounge'
                    : fromSoloPunchcard
                    ? 'Back to Punchcard'
                    : fromMain
                    ? 'Back to Main'
                    : fromStarterKit
                    ? 'Back to Starter Kits'
                    : 'Back to lobby'}
                </Button>
                {reportedInaccuracies?.length ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setOpenReportInaccuracyModal(true)}
                  >
                    Reported Inaccuracies
                  </Button>
                ) : null}
                {replayUrl && (
                  <Button variant="outlined" onClick={() => navigate(`/replay/${gameId}`)}>
                    Watch Replay
                  </Button>
                )}
              </Stack>
            </Stack>
          ) : null}
          {mode === 'teams' ? (
            <Stack
              direction={'row'}
              justifyContent={'center'}
              spacing={4}
              marginY={1}
            >
              <ToggleStats
                handleShowStatsChange={handleShowStatsChange}
                toggleQuestionsStats={toggleQuestionsStats}
              />
            </Stack>
          ) : null}
          {mode === 'teams' &&
          toggleQuestionsStats === 'stats' &&
          detailedPlayers &&
          myTeam &&
          otherTeam ? (
            <Box mt={2} display={'flex'} justifyContent={'center'}>
              <ScoreCardStats
                detailedPlayers={detailedPlayers}
                league={league}
                myTeam={myTeam}
                otherTeam={otherTeam}
              />
            </Box>
          ) : null}
          {toggleQuestionsStats === 'scorecard' && detailedPlayerSelf ? (
            <ScorecardMulti
              averageDifficulty={averageDifficulty}
              bannedCategories={bannedCategories}
              categorySelections={categorySelections}
              categorySet={categorySet}
              centerSnackbar
              detailedPlayers={detailedPlayers}
              demons={demons}
              demonsVanquished={demonsVanquished}
              gameId={gameId}
              hideTitle={mode === 'teams'}
              isDaily={daily}
              enableReportInaccuracy={
                league || tournament || !!starterKit ? false : true
              }
              gameType={gameType}
              handicap={handicap}
              handleOpenChatClick={handleOpenChatClick}
              handleMmrResultsClick={handleMmrResultsClick}
              handleReportInaccuracyRowClick={handleReportInaccuracyRowClick}
              isGhostChallenge={isGhostChallenge}
              isMatchMaking={isMatchMaking}
              league={league}
              mode={mode}
              myTeam={myTeam}
              otherTeam={otherTeam}
              profileId={profile.id}
              ranked={ranked}
              reportedInaccuracies={reportedInaccuracies}
              seededBy={seedDetails?.submittedBy}
              selectedPlayer={selectedPlayer}
              showDislike={!spectate && !starterKit && !tournament && !league}
              showRatings={!spectate && !starterKit && !tournament && !league}
              questionSet={questionSet}
              showChat={!daily}
              showMmrResults
              starterKit={starterKit}
              survivalMode={survivalMode}
              tournament={tournament}
              unreadChat={unreadChat}
              variant={variant}
            />
          ) : null}
        </Box>
      </Box>
      {!tournament && mode === 'sit-n-go' ? (
        <LeaderboardDrawer
          cutoff={cutoff}
          gameOver={gameOver}
          handlePoolLeaderboardUsernameClick={
            handlePoolLeaderboardUsernameClick
          }
          poolLeaderboardDrawerWidth={poolLeaderboardDrawerWidth}
          profileId={profile.id}
          roundOver={roundOver}
          sortedPlayers={sortedPlayers}
        />
      ) : null}
      {tournament && mode === 'sit-n-go' ? (
        <TournamentLeaderboardDrawer
          cutoff={cutoff}
          handlePoolLeaderboardUsernameClick={
            handlePoolLeaderboardUsernameClick
          }
          leaderboardDrawerWidth={poolLeaderboardDrawerWidth}
          openBottomDrawer={openBottomDrawer}
          profileId={profile.id}
          tournamentTotalPlayers={tournamentTotalPlayers}
        />
      ) : null}
      {!openBottomDrawer ? (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
          }}
        >
          <Box
            sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <Stack
              direction={'row'}
              sx={{
                width: '500px',
                backgroundColor: '#1A2027',
                display: 'flex',
                justifyContent: 'center',
                border: `1px solid ${theme.palette.primary.dark}`,
                borderBottom: 'none',
                '&:hover': {
                  backgroundColor: '#8f7462',
                },
              }}
            >
              <Tooltip title="Press [Enter] to open" placement="top">
                <Button fullWidth onClick={handleOpenBottomDrawerClick}>
                  <Box
                    sx={{
                      width: '25%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <BlinkingChatButton
                      handleOpenChatClick={handleOpenBottomDrawerClick}
                      unreadChat={unreadChat}
                    />
                  </Box>
                  <Box sx={{ width: '50%' }}>Open</Box>
                  <Box sx={{ width: '25%' }}>
                    <Badge
                      badgeContent={lobbyOpenGames?.length}
                      color="secondary"
                    >
                      Quick Join
                    </Badge>
                  </Box>
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      ) : null}
      <Drawer
        variant="persistent"
        anchor="bottom"
        open={openBottomDrawer}
        sx={{
          '& .MuiPaper-root': {
            borderTop: 'none',
            backgroundColor: 'transparent',
          },
        }}
      >
        <Stack>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '300px',
                backgroundColor: '#1A2027',
                border: `1px solid ${theme.palette.primary.dark}`,
                borderBottom: 'none',
                '&:hover': {
                  backgroundColor: '#8f7462',
                },
              }}
            >
              <Tooltip title="Press [ESC] to close" placement="top">
                <Button
                  fullWidth
                  onClick={handleCloseBottomDrawerClick}
                  size="small"
                >
                  Close
                </Button>
              </Tooltip>
            </Box>
          </Box>
          <BottomDrawer
            chat={chat}
            focusInput={focusInput}
            gameId={gameId}
            handleBackToTournamentLobbyClick={handleBackToTournamentLobbyClick}
            handleJoinGameClick={handleJoinGameClick}
            handleLeaveGameClick={handleLeaveGameClick}
            hideChat={isGhostChallenge || isMatchMaking}
            isDrawerOpen={openBottomDrawer}
            lobbyOpenGames={lobbyOpenGames}
            openedBottomDrawerByClick={openedBottomDrawerByClick}
            profileId={profile.id}
            socket={socket}
            spectate={spectate}
            tournament={tournament}
            username={profile.username}
            usersInRoom={getUsersInRoom()}
          />
        </Stack>
      </Drawer>
    </>
  );
};
